/**
 * Control Inheritance — Type Definitions
 */

import type { FormControl } from '@atlas-forms/types-js';

/**
 * Fields that mark a control as inherited from another form.
 *
 * controlInheritType = "override" (default)
 *   One base control is fetched by referredControlID (exactly one ID), deep-merged
 *   with the local definition (derived always wins), and replaces this control 1-for-1.
 *
 * controlInheritType = "import"
 *   The local control definition is IGNORED completely.
 *   referredControlID can be:
 *     - A single control ID          → imports that one control as-is
 *     - Comma-separated IDs          → imports each named control as-is, in order
 *     - "*"                          → imports ALL controls from the base form as-is
 *   The placeholder control is replaced by 1..N controls from the base form.
 *   No merging occurs.
 *
 * Note: only single-level resolution is performed. If an imported control is itself
 * inherited, its referredFormID/referredControlID markers are left in place and a
 * warning is emitted. Recursive resolution is not supported.
 */
export interface InheritedControlDef extends Record<string, unknown> {
  /** Numeric FormID of the form that owns the base control(s). */
  referredFormID: number;
  /**
   * In "override" mode: exactly one control ID (non-empty).
   * In "import"   mode: a single control ID, comma-separated IDs, or "*" for all.
   */
  referredControlID: string;
  /** Default: "override". */
  controlInheritType?: 'override' | 'import';
}

export const isInheritedControl = (ctrl: FormControl): ctrl is FormControl & InheritedControlDef => {
  const c = ctrl as unknown as Record<string, unknown>;
  return (
    typeof c.referredFormID === 'number' &&
    typeof c.referredControlID === 'string' &&
    (c.referredControlID as string).trim() !== ''
  );
};

/** controlId → error message */
export type InheritanceErrors = Record<string, string>;

/** Synthetic control injected when resolution fails — rendered as an error placeholder by FormField. */
export const INHERITANCE_ERROR_TYPE = '_inheritance-error' as const;

export interface InheritanceErrorControl {
  id: string;
  type: typeof INHERITANCE_ERROR_TYPE;
  _errorMessage: string;
  label: string;
}
